import * as url from 'url';
import AppInsightsService from '../app-insights/service';
import { SeverityLevel } from '../app-insights/severity-level';
import {
  API_LANGUAGE_CODE_EN_CA,
  API_LANGUAGE_CODE_FR_CA,
  BASE_URL_ACCOUNT_SETUP_EMAIL_API,
  BASE_URL_API,
  BASE_URL_CHECK_DUPLICATE_API,
  BASE_URL_EMAIL_API,
  ERROR_LOG_EMAIL_RECIPIENTS,
  LANGUAGE_CODE_FR_CA,
  NEW_BASE_URL_API,
  ONGUARD_ERROR_LOG_EMAIL_SENDER
} from '../constants';
import { EmailNameAddress } from '../globals';
import { HttpError } from '../globals/HttpError';
import { HttpTimeoutError } from '../globals/HttpTimeoutError';
import { default as i18n } from '../locales';
import * as AppUtils from '../utils';
import * as LogFormatters from './formatters';
import * as ApiModel from './model';
import * as ApiUtils from './utils';

const HTTP_METHOD_GET = 'GET';
const HTTP_METHOD_POST = 'POST';

// const URL_VERIFY_ACTIVATION_TOKEN = url.resolve(BASE_URL_API, 'verifyToken/');
// const URL_SUBMIT_ENROLLMENT = url.resolve(BASE_URL_API, 'enroll/Watson');
const URL_CONFIRM_ENROLLMENT = url.resolve(BASE_URL_API, 'enroll/uid/');
const URL_INTX_GOTO_WEB = url.resolve(BASE_URL_API, 'intx/gotoweb/');

const URL_SUBMIT_IDASSIST_ENROLLMENT =  url.resolve(NEW_BASE_URL_API,'enrollInclusive');
const URL_VERIFY_ACTIVATION_TOKEN = url.resolve(NEW_BASE_URL_API, 'verifyToken');
// default fetch timeout (in milliseconds)
const DEFAULT_FETCH_TIMEOUT = 120000; // 120 seconds

// default fetch mode
const DEFAULT_FETCH_MODE: RequestMode = 'cors';

// default HTTP status code to use to determine success
const DEFAULT_STATUS_CODE_SUCCESS = 200;

// default HTTP method
const DEFAULT_HTTP_METHOD = HTTP_METHOD_GET;

const DEFAULT_GET_REQUEST_HEADERS = {
  Accept: 'application/json'
};

const DEFAULT_POST_REQUEST_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
};

// error log formatters (priority-wise)
const API_ERROR_LOG_FORMATTERS = [
  LogFormatters.ApiErrorResponseFormatter,
  LogFormatters.ErrorObjectFormatter,
  LogFormatters.JsonStringifyFormatter
];

const EMPTY_OK_RESPONSE = new Response('', {
  status: 200,
  statusText: 'OK',
  headers: new Headers({ 'Content-Length': '0' })
});

export const CONFIRM_ENROLL_RESPONSE_NULL_EID: ApiModel.ConfirmEnrollmentApiSuccessResponse = {
  eid: null,
  returncode: 0
};

function getDefaultRequestHeaders(method: string): Headers {
  let defaultHeaders = {};
  if (method === HTTP_METHOD_GET) {
    defaultHeaders = DEFAULT_GET_REQUEST_HEADERS;
  } else if (method === HTTP_METHOD_POST) {
    defaultHeaders = DEFAULT_POST_REQUEST_HEADERS;
  }
  return new Headers(
    Object.assign({}, defaultHeaders, {
      'Accept-Language': i18n.language === LANGUAGE_CODE_FR_CA ? API_LANGUAGE_CODE_FR_CA : API_LANGUAGE_CODE_EN_CA//,
      //'X-ApiKey': process.env.REACT_APP_API_KEY || ''
    })
  );
}

function getDefaultSendEmailApiRequestHeaders(): Headers {
  return new Headers(
    Object.assign({}, DEFAULT_POST_REQUEST_HEADERS, {
      'Accept-Language': i18n.language === LANGUAGE_CODE_FR_CA ? API_LANGUAGE_CODE_FR_CA : API_LANGUAGE_CODE_EN_CA//,
      //'X-ApiKey': process.env.REACT_APP_SEND_EMAIL_API_KEY || ''
    })
  );
}

export async function sendEmail(body: ApiModel.SendEmailApiData, headers?: Headers) {
  const requestUrl = BASE_URL_EMAIL_API;
  const mode = 'cors';
  const method = HTTP_METHOD_POST;
  headers = headers || getDefaultSendEmailApiRequestHeaders();
  const requestBody = AppUtils.jsonStringify(body);
  return fetch(requestUrl, { mode, method, headers, body: requestBody, cache: 'no-store' });
}

export async function sendAccountSetupEmail(body: ApiModel.SendAccountSetupEmailApiData, headers?: Headers) {
  const requestUrl = BASE_URL_ACCOUNT_SETUP_EMAIL_API;
  const mode = 'cors';
  const method = HTTP_METHOD_POST;
  headers = headers || getDefaultSendEmailApiRequestHeaders();
  const requestBody = AppUtils.jsonStringify(body);
  return fetch(requestUrl, { mode, method, headers, body: requestBody, cache: 'no-store' });
}

export async function checkDuplicate(body: ApiModel.checkDuplicateData, headers?: Headers) {
  const apiUrl = BASE_URL_CHECK_DUPLICATE_API;
  try {
    const requestBody = AppUtils.jsonStringify(body);
    return await apiPostRequest(apiUrl, { body: requestBody });
  } catch (error) {
    let responseJson;
    if (AppUtils.objectHasOwnKeys(error, ['response'])) {
      responseJson = await AppUtils.tryGetResponseJson(error.response);
    }
    throw responseJson;
  }
}

// tslint:disable-next-line:no-any
async function sendErrorLogEmail(error: any, emailRecipients?: EmailNameAddress[], errorLogExtraData?: any) {
  if (error != null && Array.isArray(emailRecipients) && emailRecipients.length > 0) {
    try {
      const HtmlContent: string[] = [];
      for (const formatter of API_ERROR_LOG_FORMATTERS) {
        const content = await formatter(error, errorLogExtraData);
        if (content !== null) {
          content.forEach((item) => HtmlContent.push(item));
          break;
        }
      }

      if (HtmlContent.length > 0) {
        HtmlContent.push(`<p><strong>Date and Time:</strong> ${new Date().toUTCString()}`);
        const body: ApiModel.SendEmailApiData = {
          From: ONGUARD_ERROR_LOG_EMAIL_SENDER,
          To: emailRecipients,
          Subject: `OnGuard API Error Log`,
          HtmlContent: HtmlContent.join('\n')
        };
        return sendEmail(body);
      }
    } catch (ex) {
      AppInsightsService.trackException({ exception: ex, severityLevel: SeverityLevel.Warning });
      if (process.env.REACT_APP_ENV !== 'production') {
        console.log(`ERROR: ApiClient::sendErrorLogEmail: ${ex}`);
      }
      /* ignore */
    }
  }
  return Promise.resolve(EMPTY_OK_RESPONSE);
}

type ApiRequestOptions<TRequestBody = string> = ApiModel.ApiRequestOptions<TRequestBody> & {
  method?: string;
  mode?: RequestMode;
};

export async function apiRequest(apiUrl: string, options?: ApiRequestOptions) {
  const getOption =
    typeof options === 'undefined'
      ? <T>(_: keyof ApiRequestOptions, defaultValue: T): T => defaultValue
      : <T>(option: keyof ApiRequestOptions, defaultValue: T): T => {
          return typeof options[option] === 'undefined' ? defaultValue : (options[option] as T);
        };

  const mode = getOption<RequestMode>('mode', DEFAULT_FETCH_MODE);
  const method = getOption<string>('method', DEFAULT_HTTP_METHOD);
  const headers = getOption<Headers>('headers', getDefaultRequestHeaders(method));
  const body = getOption<BodyInit | null>('body', null);
  const statusOnSuccess = getOption<number>('statusOnSuccess', DEFAULT_STATUS_CODE_SUCCESS);
  const timeout = getOption<number>('responseTimeout', DEFAULT_FETCH_TIMEOUT);

  const startTime = AppUtils.getTimestamp();
  let statusCode = 0;
  try {
    const response = await AppUtils.waitFor(fetch(apiUrl, { mode, method, headers, body, cache: 'no-store' }), timeout);

    statusCode = response.status;
    if (statusCode === statusOnSuccess) {
      return response;
    }

    throw new HttpError(response);
  } catch (error) {
    let exception = error;

    if (error instanceof Error && error.message === 'Timeout') {
      exception = new HttpTimeoutError(timeout);
    }

    AppInsightsService.trackException({
      exception,
      severityLevel: SeverityLevel.Critical
    });

    const errorLogRecipients = getOption<EmailNameAddress[] | undefined>('errorLogRecipients', undefined);
    const errorLogExtraData = getOption<any>('errorLogExtraData', undefined);
    sendErrorLogEmail(exception, errorLogRecipients, errorLogExtraData);

    throw exception;
  } finally {
    const duration = AppUtils.getTimestamp() - startTime;
    AppInsightsService.trackAjaxRequest({
      name: `${method} ${apiUrl}`,
      method,
      url: apiUrl,
      duration,
      resultCode: statusCode,
      success: statusCode === statusOnSuccess
    });
  }
}

export async function apiGetRequest(apiUrl: string, options?: ApiRequestOptions) {
  options = Object.assign({}, options, { method: HTTP_METHOD_GET });
  return apiRequest(apiUrl, options);
}

export async function apiPostRequest(apiUrl: string, options?: ApiRequestOptions) {
  options = Object.assign({}, options, { method: HTTP_METHOD_POST });
  return apiRequest(apiUrl, options);
}

export async function verifyActivationToken(options: ApiModel.ApiRequestOptions<ApiModel.VerifyActivationTokenApiData>) {
  const apiUrl = URL_VERIFY_ACTIVATION_TOKEN;
  const { body, ...otherOptions } = options;

  const errorLogData: ApiModel.VerifyActivationTokenApiErrorLogExtraData = {
    'Request Url': apiUrl,
    'Activation Token': body ? body.activationToken : ''
  };
  otherOptions.errorLogExtraData = Object.assign({}, errorLogData, options.errorLogExtraData);
  otherOptions.errorLogRecipients = options.errorLogRecipients || ERROR_LOG_EMAIL_RECIPIENTS.verifyToken;

  const requestBody = AppUtils.jsonStringify(body);
  const response = await apiPostRequest(apiUrl, { body: requestBody, ...otherOptions });
  const responseJson = await AppUtils.tryGetResponseJson<ApiModel.VerifyActivationTokenApiSuccessResponse>(response);
  return responseJson || { activated: false };
}

export async function submitEnrollment(options: ApiModel.ApiRequestOptions<ApiModel.SubmitEnrollmentApiData>) {
  const apiUrl = URL_SUBMIT_IDASSIST_ENROLLMENT;
  const { body, errorLogRecipients, errorLogExtraData, ...otherOptions } = options;
  try {
    const requestBody = AppUtils.jsonStringify(body);
    return await apiPostRequest(apiUrl, { body: requestBody, ...otherOptions });
  } catch (error) {
    const errorLogData: ApiModel.SubmitEnrollmentApiErrorLogExtraData = {
      'Request Url': apiUrl,
      'Activation Token': body ? body.ActivationToken : ''
    };

    if (AppUtils.objectHasOwnKeys(error, ['response'])) {
      const responseJson = await AppUtils.tryGetResponseJson(error.response);
      if (ApiUtils.isErrorResponse(responseJson) && responseJson.Errors.length > 0) {
        const firstError = responseJson.Errors[0];
        const keyPrefix = 'model.';
        if (AppUtils.isString(firstError.Key) && firstError.Key.startsWith(keyPrefix)) {
          const keyName = firstError.Key.substr(keyPrefix.length);
          if (body && AppUtils.objectHasOwnKeys(body, [keyName])) {
            errorLogData[keyName] = body[keyName];
          }
        }
      }
    }

    sendErrorLogEmail(
      error,
      errorLogRecipients || ERROR_LOG_EMAIL_RECIPIENTS.submitEnroll,
      Object.assign({}, errorLogData, errorLogExtraData)
    );

    throw error;
  }
}

export async function confirmEnrollment(options: ApiModel.ApiRequestOptions<ApiModel.ConfirmEnrollmentApiData>) {
  const apiUrl = URL_CONFIRM_ENROLLMENT;
  const { body, ...otherOptions } = options;

  const errorLogData: ApiModel.ConfirmEnrollmentApiErrorLogExtraData = {
    'Request Url': apiUrl,
    Regno: body ? body.regNo : '',
    IntxErrorCode: 0
  };
  otherOptions.errorLogExtraData = Object.assign({}, errorLogData, options.errorLogExtraData);
  otherOptions.errorLogRecipients = options.errorLogRecipients || ERROR_LOG_EMAIL_RECIPIENTS.confirmEnroll;
  const requestBody = AppUtils.jsonStringify(body);
  const response = await apiPostRequest(apiUrl, { body: requestBody, ...otherOptions });
  const responseJson =
    (await AppUtils.tryGetResponseJson<ApiModel.ConfirmEnrollmentApiSuccessResponse>(response)) || CONFIRM_ENROLL_RESPONSE_NULL_EID;
  if (typeof responseJson.eid !== 'string') {
    errorLogData.IntxErrorCode = responseJson.returncode;
    const exception = new Error('INTX returned a null eToken.');
    AppInsightsService.trackException({ exception, severityLevel: SeverityLevel.Warning });
    sendErrorLogEmail(exception, otherOptions.errorLogRecipients, otherOptions.errorLogExtraData);
  }
  responseJson.regNo = body ? body.regNo : '';
  return responseJson;
}

export async function intxGotoWeb(options: ApiModel.ApiRequestOptions<ApiModel.IntxGotoWebApiData>) {
  const apiUrl = URL_INTX_GOTO_WEB;
  const { body, ...otherOptions } = options;

  const errorLogData: ApiModel.IntxGotoWebApiErrorLogExtraData = {
    'Request Url': apiUrl,
    Regno: body ? body.Regno : '',
    IntxUniqueId: body ? body.IntxUniqueId : ''
  };
  otherOptions.errorLogExtraData = Object.assign({}, errorLogData, options.errorLogExtraData);
  otherOptions.errorLogRecipients = options.errorLogRecipients || ERROR_LOG_EMAIL_RECIPIENTS.confirmEnroll;

  const requestBody = AppUtils.jsonStringify(body);
  return apiPostRequest(apiUrl, { body: requestBody, ...otherOptions });
}
